import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  UserPlus,
  ArrowRightLeft,
  CheckCircle2,
  Bell,
  BellOff,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { apiGetEmployees, getTenantSlug } from "@/lib/api";
import {
  companyEmployeeToTeamMember,
  type TeamMember,
  type CompanyEmployee,
} from "./mockData";

// ── Types ─────────────────────────────────────────────────────────────────────

type EmployeeStatus = "unassigned" | "in-team" | "other-team";

interface EmployeeRow extends CompanyEmployee {
  status: EmployeeStatus;
}

// ── LM Transfer Confirmation Panel ────────────────────────────────────────────

interface TransferPanelProps {
  employee: EmployeeRow;
  onConfirm: () => void;
  onCancel: () => void;
}

function TransferPanel({ employee, onConfirm, onCancel }: TransferPanelProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  return (
    <div className="border border-amber-500/30 bg-amber-500/5 rounded-xl p-3 mt-2 space-y-2">
      <p className="text-xs font-medium text-amber-400">
        {t("teamOverview.transferRequestFor", { name: employee.name })}
      </p>
      <p className="text-[11px] text-muted-foreground">
        {t("teamOverview.transferRequestDesc", {
          from: employee.lineManagerName ?? "—",
        })}
      </p>
      <textarea
        className="w-full text-xs rounded-lg border border-border bg-background px-3 py-2 resize-none min-h-[56px]"
        placeholder={t("teamOverview.transferNotePlaceholder")}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex gap-2 justify-end">
        <Button
          size="sm"
          variant="ghost"
          className="text-xs h-7"
          onClick={onCancel}
        >
          {t("common.cancel")}
        </Button>
        <Button
          size="sm"
          className="text-xs h-7 gradient-brand text-primary-foreground"
          onClick={onConfirm}
        >
          {t("teamOverview.sendRequest")}
        </Button>
      </div>
    </div>
  );
}

// ── HR Reassign View ──────────────────────────────────────────────────────────

function HRView({
  currentTeamIds,
  onClose,
}: {
  currentTeamIds: Set<number>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [notify, setNotify] = useState(true);
  const [saved, setSaved] = useState<Set<number>>(new Set());
  const [selectedLM, setSelectedLM] = useState<Record<number, string>>({});

  const { data: apiEmployees = [] } = useQuery({
    queryKey: ["employees", getTenantSlug()],
    queryFn: apiGetEmployees,
  });

  const allEmployees = useMemo<CompanyEmployee[]>(
    () =>
      apiEmployees.map((e) => ({
        id: Number(e.id),
        name: e.name,
        initials: e.initials,
        avatarColor: e.avatarColor,
        role: e.jobTitle,
        department: e.department,
        lineManagerId: e.reportsTo ? Number(e.reportsTo.id) : null,
        lineManagerName: e.reportsTo?.name ?? null,
      })),
    [apiEmployees],
  );

  const lmOptions = useMemo(
    () => apiEmployees.filter((e) => e.isLineManager),
    [apiEmployees],
  );

  const filtered = allEmployees.filter((e) => {
    const q = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      e.role.toLowerCase().includes(q) ||
      e.department.toLowerCase().includes(q)
    );
  });

  function save(employeeId: number) {
    setSaved((prev) => new Set([...prev, employeeId]));
  }

  function currentLMName(e: CompanyEmployee): string {
    if (e.lineManagerName) return e.lineManagerName;
    return t("teamOverview.noLineManager");
  }

  return (
    <div className="space-y-4">
      {/* Notify toggle */}
      <div className="flex items-center gap-2 rounded-xl border border-border/60 px-3 py-2.5 bg-muted/30">
        <button
          className={cn(
            "flex items-center gap-2 text-xs font-medium flex-1",
            notify ? "text-foreground" : "text-muted-foreground",
          )}
          onClick={() => setNotify(true)}
        >
          <Bell className={cn("h-3.5 w-3.5", notify && "text-indigo-400")} />
          {t("teamOverview.sendNotification")}
        </button>
        <div className="w-px h-4 bg-border/60" />
        <button
          className={cn(
            "flex items-center gap-2 text-xs font-medium flex-1",
            !notify ? "text-foreground" : "text-muted-foreground",
          )}
          onClick={() => setNotify(false)}
        >
          <BellOff className={cn("h-3.5 w-3.5", !notify && "text-amber-400")} />
          {t("teamOverview.updateSilently")}
        </button>
      </div>

      <Input
        placeholder={t("teamOverview.searchEmployees")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-sm"
      />

      {/* Column headers */}
      <div className="grid grid-cols-[1fr_140px_140px_80px] gap-3 px-3 pb-1">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("teamOverview.employee")}</p>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("teamOverview.currentLMCol")}</p>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("teamOverview.assignTo")}</p>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide text-right">{t("teamOverview.action")}</p>
      </div>

      <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
        {filtered.map((e) => {
          const isSaved = saved.has(e.id);
          const currentLm = currentLMName(e);
          const lmVal = selectedLM[e.id] ?? "";
          return (
            <div
              key={e.id}
              className="grid grid-cols-[1fr_140px_140px_80px] items-center gap-3 rounded-xl border border-border/50 bg-card px-3 py-2.5"
            >
              {/* Avatar + name/role */}
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-primary-foreground shrink-0"
                  style={{ background: e.avatarColor }}
                >
                  {e.initials}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">{e.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {e.role} · {e.department}
                  </p>
                </div>
              </div>

              {/* Current LM */}
              <p className="text-[10px] text-muted-foreground truncate" title={currentLm}>
                {currentLm}
              </p>

              {/* New LM selector */}
              <div>
                {isSaved ? (
                  <span className="text-[10px] text-muted-foreground">—</span>
                ) : (
                  <select
                    className="w-full text-[11px] rounded-lg border border-border bg-background px-1.5 py-1"
                    value={lmVal}
                    onChange={(ev) =>
                      setSelectedLM((p) => ({ ...p, [e.id]: ev.target.value }))
                    }
                  >
                    <option value="">{t("teamOverview.selectLM")}</option>
                    <option value="none">{t("teamOverview.noLineManager")}</option>
                    {lmOptions.map((lm) => (
                      <option key={lm.id} value={String(lm.id)}>
                        {lm.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Save */}
              <div className="flex justify-end">
                {isSaved ? (
                  <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/40 gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {t("teamOverview.saved")}
                  </Badge>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2"
                    disabled={!lmVal}
                    onClick={() => save(e.id)}
                  >
                    {t("teamOverview.applyChange")}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-center text-xs text-muted-foreground py-6">
            {t("teamOverview.noMatchingEmployees")}
          </p>
        )}
      </div>

      <div className="flex justify-end pt-2 border-t border-border/40">
        <Button
          size="sm"
          className="text-xs gradient-brand text-primary-foreground"
          onClick={onClose}
        >
          {t("common.done")}
        </Button>
      </div>
    </div>
  );
}

// ── LM Add View ───────────────────────────────────────────────────────────────

function LMView({
  currentTeamIds,
  onAddMember,
}: {
  currentTeamIds: Set<number>;
  onAddMember: (m: TeamMember) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [transferTarget, setTransferTarget] = useState<number | null>(null);
  const [added, setAdded] = useState<Set<number>>(new Set());
  const [transferred, setTransferred] = useState<Set<number>>(new Set());

  const { data: apiEmployees = [] } = useQuery({
    queryKey: ["employees", getTenantSlug()],
    queryFn: apiGetEmployees,
  });

  const pool = useMemo<EmployeeRow[]>(
    () =>
      apiEmployees
        .filter((e) => !currentTeamIds.has(Number(e.id)))
        .map((e): EmployeeRow => ({
          id: Number(e.id),
          name: e.name,
          initials: e.initials,
          avatarColor: e.avatarColor,
          role: e.jobTitle,
          department: e.department,
          lineManagerId: e.reportsTo ? Number(e.reportsTo.id) : null,
          lineManagerName: e.reportsTo?.name ?? null,
          status: e.reportsTo ? "other-team" : "unassigned",
        })),
    [apiEmployees, currentTeamIds],
  );

  const filtered = pool.filter((e) => {
    const q = search.toLowerCase();
    return (
      e.name.toLowerCase().includes(q) ||
      e.role.toLowerCase().includes(q) ||
      e.department.toLowerCase().includes(q)
    );
  });

  function doAdd(e: EmployeeRow) {
    const member = companyEmployeeToTeamMember(e, 1);
    onAddMember(member);
    setAdded((prev) => new Set([...prev, e.id]));
  }

  function doTransfer(emp: EmployeeRow) {
    setTransferred((prev) => new Set([...prev, emp.id]));
    setTransferTarget(null);
  }

  return (
    <div className="space-y-3">
      <Input
        placeholder={t("teamOverview.searchEmployees")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-sm"
      />

      {/* Column headers */}
      <div className="grid grid-cols-[1fr_160px_140px] gap-3 px-3 pb-1">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("teamOverview.employee")}</p>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{t("teamOverview.currentTeam")}</p>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide text-right">{t("teamOverview.action")}</p>
      </div>

      <div className="space-y-1.5 max-h-[380px] overflow-y-auto pr-1">
        {filtered.map((e) => {
          const isAdded = added.has(e.id);
          const isTransferred = transferred.has(e.id);
          const isTransferring = transferTarget === e.id;
          const isUnassigned = e.status === "unassigned";

          return (
            <div key={e.id} className="space-y-0">
              <div className="grid grid-cols-[1fr_160px_140px] items-center gap-3 rounded-xl border border-border/50 bg-card px-3 py-2.5">
                {/* Avatar + name/role */}
                <div className="flex items-center gap-2.5 min-w-0">
                  <div
                    className="h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-semibold text-primary-foreground shrink-0"
                    style={{ background: e.avatarColor }}
                  >
                    {e.initials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{e.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {e.role} · {e.department}
                    </p>
                  </div>
                </div>

                {/* Current team / manager */}
                <div className="min-w-0">
                  {isAdded || isTransferred ? (
                    <span className="text-[10px] text-muted-foreground">—</span>
                  ) : isUnassigned ? (
                    <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/40">
                      {t("teamOverview.unassigned")}
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-muted-foreground truncate block" title={e.lineManagerName ?? "—"}>
                      {e.lineManagerName ?? "—"}
                    </span>
                  )}
                </div>

                {/* Action */}
                <div className="flex justify-end">
                  {isAdded ? (
                    <Badge variant="outline" className="text-[10px] text-emerald-500 border-emerald-500/40 gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {t("teamOverview.addedToTeam")}
                    </Badge>
                  ) : isTransferred ? (
                    <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/40 gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {t("teamOverview.requestSent")}
                    </Badge>
                  ) : isUnassigned ? (
                    <Button
                      size="sm"
                      className="h-7 text-[11px] px-2.5 gradient-brand text-primary-foreground gap-1"
                      onClick={() => doAdd(e)}
                    >
                      <UserPlus className="h-3 w-3" />
                      {t("teamOverview.addToTeam")}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px] px-2.5 gap-1"
                      onClick={() => setTransferTarget(isTransferring ? null : e.id)}
                    >
                      <ArrowRightLeft className="h-3 w-3" />
                      {t("teamOverview.requestTransfer")}
                    </Button>
                  )}
                </div>
              </div>

              {isTransferring && (
                <TransferPanel
                  employee={e}
                  onConfirm={() => doTransfer(e)}
                  onCancel={() => setTransferTarget(null)}
                />
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <p className="text-center text-xs text-muted-foreground py-6">
            {t("teamOverview.noMatchingEmployees")}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main Dialog ───────────────────────────────────────────────────────────────

interface AddEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  currentTeamIds: Set<number>;
  isHR: boolean;
  isLineManager: boolean;
  onAddMember: (member: TeamMember) => void;
}

export function AddEmployeeDialog({
  open,
  onClose,
  currentTeamIds,
  isHR,
  isLineManager,
  onAddMember,
}: AddEmployeeDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-2">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-indigo-500/10">
              <UserPlus className="h-4 w-4 text-indigo-400" />
            </div>
            <div>
              <DialogTitle className="text-sm font-semibold">
                {isHR
                  ? t("teamOverview.manageAssignmentsTitle")
                  : t("teamOverview.addEmployeeTitle")}
              </DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                {isHR
                  ? t("teamOverview.manageAssignmentsSubtitle")
                  : t("teamOverview.addEmployeeSubtitle")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {isHR ? (
          <HRView currentTeamIds={currentTeamIds} onClose={onClose} />
        ) : (
          <LMView currentTeamIds={currentTeamIds} onAddMember={onAddMember} />
        )}
      </DialogContent>
    </Dialog>
  );
}
